import React, {useState} from "react";
import {Card, Table, Button, Space, Tooltip, Row, message} from "antd";
import {SearchOutlined, CloseOutlined} from "@ant-design/icons";
import {useStore} from "../../../../hooks/useStore";
import {useFetch} from "../../../../hooks/useFetch";
import {Link} from "react-router-dom";
import {BackofficePendingPaymentsFilters} from "./BackofficePendingPaymentsFilters";
import moment from "moment";
import {codPaymentStatuses, codPaymentTypes} from "../../../../constants/constants";
import {isConstantExist} from "../../../../utils/helpers";
import {useTranslation} from "react-i18next";
const PAGE_SIZE = 10;
const formatDate = date => {
    return moment(date).format("DD.MM.YYYY HH:mm");
};
export const BackOfficePendingPaymentsTab = ({isTabOpened = false}) => {
    const stores = useStore();
    const {t} = useTranslation();
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [recall, setRecall] = useState(false);
    const [checkPaymentStatusOnPosLoading, setCheckPaymentStatusOnPosLoading] = useState(false);
    const [loading, payments] = useFetch({
        fetchFn: stores.codPaymentStore.getPendingPayments.bind(null, {
            ...filters,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE
        }),
        dependencies: [filters, page, isTabOpened, recall],
        initialCall: isTabOpened,
        condition: !stores.meStore.checkRole("isCompanyUser") && isTabOpened
    });

    const handleCheckPaymentStatusOnPos = async record => {
        setCheckPaymentStatusOnPosLoading(true);
        try {
            const result = await stores.codPaymentStore.checkPaymentStatusOnPos({
                codPaymentId: record._id
            });
            if (result) {
                message.success("Ödeme durumu başarıyla sorgulandı");
                setRecall(!recall);
            } else {
                message.error("Ödeme durumu sorgulanırken bir hata oluştu");
            }
        } catch (error) {
            console.log(error);
            message.error("Ödeme durumu sorgulanırken bir hata oluştu");
        } finally {
            setCheckPaymentStatusOnPosLoading(false);
        }
    };

    const columns = [
        {
            title: "Firma",
            dataIndex: ["deliveryInfo", "order", "pickupInfoCompanyName"],
            key: "company",
            render: (text, row) => (
                <Link
                    data-testid="companyName"
                    target="_blank"
                    to={{
                        pathname: "/companies/" + row?.deliveryInfo?.order?.pickupInfoCompanyId
                    }}
                >
                    {text}
                </Link>
            )
        },
        {
            title: "Alt Firma",
            dataIndex: ["deliveryInfo", "order", "pickupInfoName"],
            key: "subCompany",
            render: (text, row) => (
                <Link
                    data-testid="subCompanyName"
                    target="_blank"
                    to={{
                        pathname: "/companies/sub-company/" + row?.deliveryInfo?.order?.pickupInfoId
                    }}
                >
                    {text}
                </Link>
            )
        },
        {
            title: "Ödeme Id",
            dataIndex: ["_id"],
            key: "_id",
            ellipsis: {
                showTitle: false
            },
            render: text => <Tooltip title={text}>{text}</Tooltip>
        },
        {
            title: "Tutar",
            dataIndex: ["payment", "receivedAmount"],
            key: "amount",
            render: text => text && `${text} TL`
        },
        {
            title: "İşlem Tarihi",
            dataIndex: "createdAt",
            key: "createdAt",
            render: text => text && formatDate(text)
        },
        {
            title: "Ödeme Yöntemi",
            dataIndex: ["payment", "paymentType"],
            key: "paymentMethod",
            render: paymentType =>
                `${
                    isConstantExist(codPaymentTypes, paymentType)
                        ? t(`paymentTypes.${paymentType}`, {
                              ns: "codPayment"
                          })
                        : paymentType
                }`
        },
        {
            title: "İşlem Durumu",
            dataIndex: ["status"],
            key: "status",
            render: text => {
                const status = codPaymentStatuses?.find(status => status?.value === text);
                return status?.name || text;
            }
        },
        {
            title: "Sipariş No",
            dataIndex: ["delivery", "vigoOrderNumber"],
            key: "vigoOrderNumber",
            render: (text, row) => (
                <Link
                    data-testid="vigoOrderNumber"
                    target="_blank"
                    to={{
                        pathname: "/orders/" + (row?.deliveryInfo?._id || row?.delivery?.deliveryId)
                    }}
                >
                    {text}
                </Link>
            )
        },
        {
            title: "Kurye",
            dataIndex: ["deliveryInfo", "assignedCourier", "fullname"],
            key: "courier",
            render: (text, row) => (
                <Link
                    data-testid="courierName"
                    target="_blank"
                    to={{
                        pathname: "/couriers/" + row?.deliveryInfo?.assignedCourier?._id
                    }}
                >
                    <>{text}</>
                </Link>
            )
        },
        {
            title: "Aksiyonlar",
            key: "actions",
            render: (_, record) => (
                <Space>
                    <Tooltip title="İşlem Onayı Sorgula">
                        <Button
                            loading={checkPaymentStatusOnPosLoading}
                            size="small"
                            icon={<SearchOutlined />}
                            onClick={() => handleCheckPaymentStatusOnPos(record)}
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    return (
        <Card>
            <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                <BackofficePendingPaymentsFilters filters={filters} setFilters={setFilters} />
            </Row>

            <Table
                loading={loading}
                columns={columns}
                size="small"
                dataSource={payments?.rows || []}
                rowKey="_id"
                pagination={{
                    total: payments?.dataCount || 0,
                    current: page,
                    hideOnSinglePage: true,
                    showSizeChanger: false,
                    pageSize: PAGE_SIZE,
                    onChange: page => {
                        setPage(page);
                    }
                }}
            />
        </Card>
    );
};
